// Staff addresses — who really uses which e-mail address (kanban ba007a0f).
//
// Scott, review c63b8aa2: "Check our emails to figure out what their actual
// addresses are ... sometimes there might be mappings and multiples." The
// builder (nightly, or "Rebuild from mail now") counts every staff address our
// mail showed and suggests a person for the unmapped ones; a person confirms
// or rejects here. The hand-off curator's resolver trusts confirmed rows only.
//
// Backend: app/routes/staff_addresses.py. Every control carries a data-action
// so the Guru recorder logs who confirmed what (services/guruRecorderBridge).
import { AxiosError } from 'axios';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  namesSeen,
  personNamed,
  sortByTraffic,
  staffAddressesApi,
  trafficSummary,
  type Decision,
  type StaffAddress,
  type StaffAddressMap,
  type StaffPerson,
} from '../services/staffAddresses';
import styles from './StaffAddressesPage.module.css';

function errorText(err: unknown): string {
  if (err instanceof AxiosError) {
    const detail = (err.response?.data as { detail?: unknown } | undefined)?.detail;
    if (typeof detail === 'string') return detail;
  }
  return err instanceof Error ? err.message : 'Something went wrong.';
}

function when(iso: string | null): string {
  return iso ? iso.slice(0, 10) : '—';
}

interface RowActionsProps {
  row: StaffAddress;
  people: StaffPerson[];
  busy: boolean;
  onDecide: (row: StaffAddress, status: Decision, personId?: string) => void;
}

function SuggestionActions({ row, busy, onDecide }: RowActionsProps): React.ReactElement {
  return (
    <div className={styles.actions}>
      <button
        type="button"
        className={styles.btnPrimary}
        data-action="staff_address_confirm"
        data-target-address-id={row.id}
        disabled={busy}
        onClick={() => onDecide(row, 'confirmed')}
      >
        Confirm
      </button>
      <button
        type="button"
        className={styles.btnDanger}
        data-action="staff_address_reject"
        data-target-address-id={row.id}
        disabled={busy}
        onClick={() => onDecide(row, 'rejected')}
      >
        Not them
      </button>
      <button
        type="button"
        className={styles.btn}
        data-action="staff_address_share"
        data-target-address-id={row.id}
        disabled={busy}
        onClick={() => onDecide(row, 'shared')}
      >
        Shared desk
      </button>
    </div>
  );
}

interface UnmappedActionsProps extends RowActionsProps {
  onNewPerson: (row: StaffAddress, name: string) => void;
}

function suggestedName(row: StaffAddress): string {
  const [first] = Object.entries(row.display_names).sort((x, y) => y[1] - x[1]);
  return first ? first[0] : '';
}

function UnmappedActions({
  row,
  people,
  busy,
  onDecide,
  onNewPerson,
}: UnmappedActionsProps): React.ReactElement {
  const [personId, setPersonId] = useState('');
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState(() => suggestedName(row));
  if (naming) {
    return (
      <div className={styles.actions}>
        <input
          className={styles.select}
          aria-label={`Name of the person behind ${row.address}`}
          data-action="staff_address_new_person_name"
          data-target-address-id={row.id}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <button
          type="button"
          className={styles.btnPrimary}
          data-action="staff_address_new_person_create"
          data-target-address-id={row.id}
          disabled={busy || !name.trim()}
          onClick={() => onNewPerson(row, name.trim())}
        >
          Create and confirm
        </button>
        <button
          type="button"
          className={styles.btn}
          data-action="staff_address_new_person_cancel"
          data-target-address-id={row.id}
          onClick={() => setNaming(false)}
        >
          Cancel
        </button>
      </div>
    );
  }
  return (
    <div className={styles.actions}>
      <select
        className={styles.select}
        aria-label={`Who uses ${row.address}`}
        data-action="staff_address_pick_person"
        data-target-address-id={row.id}
        value={personId}
        onChange={(e) => setPersonId(e.target.value)}
      >
        <option value="">Who is this?</option>
        {people.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </select>
      <button
        type="button"
        className={styles.btnPrimary}
        data-action="staff_address_confirm"
        data-target-address-id={row.id}
        disabled={busy || !personId}
        onClick={() => onDecide(row, 'confirmed', personId)}
      >
        Confirm
      </button>
      <button
        type="button"
        className={styles.btn}
        data-action="staff_address_new_person"
        data-target-address-id={row.id}
        disabled={busy}
        onClick={() => setNaming(true)}
      >
        New person
      </button>
      <button
        type="button"
        className={styles.btn}
        data-action="staff_address_share"
        data-target-address-id={row.id}
        disabled={busy}
        onClick={() => onDecide(row, 'shared')}
      >
        Shared desk
      </button>
    </div>
  );
}

export function StaffAddressesPage(): React.ReactElement {
  const [map, setMap] = useState<StaffAddressMap | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [showShared, setShowShared] = useState(false);

  const load = useCallback(async () => {
    try {
      setMap(await staffAddressesApi.map());
      setLoadError(null);
    } catch (err) {
      setLoadError(errorText(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const decide = useCallback(
    async (row: StaffAddress, status: Decision, personId?: string) => {
      setBusy(true);
      setActionError(null);
      try {
        await staffAddressesApi.decide(row.id, status, { personId });
        setNotice(`${row.address}: ${status}.`);
        await load();
      } catch (err) {
        setActionError(errorText(err));
      } finally {
        setBusy(false);
      }
    },
    [load],
  );

  const newPerson = useCallback(
    async (row: StaffAddress, name: string) => {
      setBusy(true);
      setActionError(null);
      try {
        const person = await staffAddressesApi.declarePerson({ name });
        await staffAddressesApi.decide(row.id, 'confirmed', { personId: person.id });
        setNotice(`${row.address}: confirmed for ${person.name} (new person).`);
        await load();
      } catch (err) {
        setActionError(errorText(err));
      } finally {
        setBusy(false);
      }
    },
    [load],
  );

  const rebuild = useCallback(async () => {
    setBusy(true);
    setActionError(null);
    try {
      const counts = await staffAddressesApi.rebuild();
      setNotice(
        `Rebuilt from mail: ${counts.observed_seen ?? 0} addresses seen, ` +
          `${counts.suggest_suggested ?? 0} suggested.`,
      );
      await load();
    } catch (err) {
      setActionError(errorText(err));
    } finally {
      setBusy(false);
    }
  }, [load]);

  const suggestions = useMemo(() => (map ? sortByTraffic(map.suggestions) : []), [map]);
  const unmapped = useMemo(() => (map ? sortByTraffic(map.unmapped) : []), [map]);
  const shared = useMemo(() => (map ? sortByTraffic(map.shared) : []), [map]);

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <div>
          <h1>Staff addresses</h1>
          <p>
            Who really uses which e-mail address, from our own mail. Confirm or reject what the
            builder suggests; Merlin&apos;s &quot;do they already know&quot; check trusts
            confirmed addresses only and says &quot;unmapped&quot; rather than guess.
          </p>
        </div>
        <button
          type="button"
          className={styles.btn}
          data-action="staff_addresses_rebuild"
          disabled={busy}
          onClick={() => void rebuild()}
        >
          Rebuild from mail now
        </button>
      </div>

      {loadError && <div className={styles.loadError}>{loadError}</div>}
      {actionError && <div className={styles.loadError}>{actionError}</div>}
      {notice && <p className={styles.notice}>{notice}</p>}
      {!map && !loadError && <div className={styles.empty}>Loading the map…</div>}

      {map && (
        <>
          <div className={styles.counts}>
            <span className={styles.count}>
              <b>{map.counts.people ?? 0}</b> people
            </span>
            <span className={styles.count}>
              <b>{map.counts.confirmed ?? 0}</b> confirmed addresses
            </span>
            <span className={styles.count}>
              <b>{map.counts.suggested ?? 0}</b> to decide
            </span>
            <span className={styles.count}>
              <b>{map.counts.unmapped ?? 0}</b> unmapped
            </span>
            <span className={styles.count}>
              <b>{map.counts.shared ?? 0}</b> shared desks
            </span>
          </div>

          <section className={styles.section} aria-labelledby="suggestions-h">
            <h2 id="suggestions-h">
              Suggestions<small>the builder thinks these belong to someone</small>
            </h2>
            {suggestions.length === 0 ? (
              <div className={styles.empty}>Nothing waiting on a decision.</div>
            ) : (
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Address</th>
                      <th>Suggested person</th>
                      <th>Why</th>
                      <th>Mail</th>
                      <th>Last seen</th>
                      <th>Decision</th>
                    </tr>
                  </thead>
                  <tbody>
                    {suggestions.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <span className={styles.addr}>{row.address}</span>
                          <div className={styles.muted}>{namesSeen(row)}</div>
                        </td>
                        <td>{personNamed(map.people, row.person_id)}</td>
                        <td className={styles.muted}>{row.suggested_reason}</td>
                        <td className={styles.muted}>{trafficSummary(row)}</td>
                        <td className={styles.muted}>{when(row.last_seen)}</td>
                        <td>
                          <SuggestionActions
                            row={row}
                            people={map.people}
                            busy={busy}
                            onDecide={decide}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className={styles.section} aria-labelledby="people-h">
            <h2 id="people-h">
              People<small>confirmed addresses, from the portal, the directory, your decisions</small>
            </h2>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Person</th>
                    <th>Portal login</th>
                    <th>Sage</th>
                    <th>Addresses</th>
                  </tr>
                </thead>
                <tbody>
                  {map.people.map((p) => (
                    <tr key={p.id}>
                      <td>
                        <b>{p.name}</b>
                      </td>
                      <td className={styles.muted}>{p.portal_email ?? '—'}</td>
                      <td className={styles.muted}>{p.sage_salesperson_no ?? '—'}</td>
                      <td>
                        {p.addresses.map((a) => (
                          <div key={a.id}>
                            <span className={styles.addr}>{a.address}</span>{' '}
                            <span className={styles.muted}>{trafficSummary(a)}</span>{' '}
                            <span className={styles.pillOk}>{a.source}</span>{' '}
                            <button
                              type="button"
                              className={styles.btn}
                              data-action="staff_address_reject"
                              data-target-address-id={a.id}
                              disabled={busy}
                              onClick={() => decide(a, 'rejected')}
                            >
                              Not theirs
                            </button>
                          </div>
                        ))}
                        {p.rejected.map((a) => (
                          <div key={a.id} className={styles.rejected}>
                            <span className={styles.addr}>{a.address}</span>{' '}
                            <span className={styles.pillMuted}>rejected</span>{' '}
                            <button
                              type="button"
                              className={styles.btn}
                              data-action="staff_address_confirm"
                              data-target-address-id={a.id}
                              disabled={busy}
                              onClick={() => decide(a, 'confirmed', p.id)}
                            >
                              Undo
                            </button>
                          </div>
                        ))}
                        {p.addresses.length === 0 && p.rejected.length === 0 && (
                          <span className={styles.muted}>no confirmed address yet</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className={styles.section} aria-labelledby="unmapped-h">
            <h2 id="unmapped-h">
              Unmapped<small>seen in our mail, nobody&apos;s yet</small>
            </h2>
            {unmapped.length === 0 ? (
              <div className={styles.empty}>Every address we saw is accounted for.</div>
            ) : (
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Address</th>
                      <th>Names the mail used</th>
                      <th>Mail</th>
                      <th>Last seen</th>
                      <th>Decision</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unmapped.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <span className={styles.addr}>{row.address}</span>
                          {row.suggested_reason && (
                            <div className={styles.pillWarn}>{row.suggested_reason}</div>
                          )}
                        </td>
                        <td className={styles.muted}>{namesSeen(row) || '—'}</td>
                        <td className={styles.muted}>{trafficSummary(row)}</td>
                        <td className={styles.muted}>{when(row.last_seen)}</td>
                        <td>
                          <UnmappedActions
                            row={row}
                            people={map.people}
                            busy={busy}
                            onDecide={decide}
                            onNewPerson={newPerson}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className={styles.section} aria-labelledby="shared-h">
            <h2 id="shared-h">
              Shared desks and systems<small>never a person&apos;s</small>{' '}
              <button
                type="button"
                className={styles.btn}
                data-action="staff_addresses_toggle_shared"
                onClick={() => setShowShared((v) => !v)}
              >
                {showShared ? 'Hide' : `Show ${shared.length}`}
              </button>
            </h2>
            {showShared && (
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Address</th>
                      <th>Why shared</th>
                      <th>Mail</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {shared.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <span className={styles.addr}>{row.address}</span>
                        </td>
                        <td className={styles.muted}>{row.suggested_reason}</td>
                        <td className={styles.muted}>{trafficSummary(row)}</td>
                        <td>
                          <button
                            type="button"
                            className={styles.btn}
                            data-action="staff_address_unshare"
                            data-target-address-id={row.id}
                            disabled={busy}
                            onClick={() => decide(row, 'unmapped')}
                          >
                            Actually a person
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

export default StaffAddressesPage;
